const AUTH_SESSION_KEY = 'helport_auth_session';
const AUTH_VIEW_KEY = 'helport_auth_view';
const AUTH_SESSION_BACKUP_KEY = 'helport_auth_session_backup';
const AUTH_VIEW_BACKUP_KEY = 'helport_auth_view_backup';

export const isApplicantUser = (user) => {
  const role = String(user?.role || '').trim().toUpperCase();
  return role === 'APPLICANT';
};

const readStorageJson = (storage, key) => {
  try {
    const raw = storage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
};

const readStorageValue = (storage, key) => {
  try {
    return storage.getItem(key) || null;
  } catch {
    return null;
  }
};

const writeStorageJson = (storage, key, value) => {
  try {
    if (value == null) {
      storage.removeItem(key);
      return;
    }
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore storage errors (private mode, quota, etc.)
  }
};

const writeStorageValue = (storage, key, value) => {
  try {
    if (value == null) {
      storage.removeItem(key);
      return;
    }
    storage.setItem(key, value);
  } catch {
    // ignore storage errors
  }
};

export const loadAuthSession = () => {
  if (typeof window === 'undefined') return null;
  return readStorageJson(window.localStorage, AUTH_SESSION_KEY)
    || readStorageJson(window.sessionStorage, AUTH_SESSION_BACKUP_KEY);
};

export const loadAuthView = () => {
  if (typeof window === 'undefined') return null;
  return readStorageValue(window.localStorage, AUTH_VIEW_KEY)
    || readStorageValue(window.sessionStorage, AUTH_VIEW_BACKUP_KEY);
};

export const saveAuthSession = (user, view = null) => {
  if (!user || typeof window === 'undefined') return;

  writeStorageJson(window.localStorage, AUTH_SESSION_KEY, user);
  writeStorageJson(window.sessionStorage, AUTH_SESSION_BACKUP_KEY, user);

  if (view) {
    writeStorageValue(window.localStorage, AUTH_VIEW_KEY, view);
    writeStorageValue(window.sessionStorage, AUTH_VIEW_BACKUP_KEY, view);
  }
};

export const saveAuthView = (view) => {
  if (!view || typeof window === 'undefined') return;

  writeStorageValue(window.localStorage, AUTH_VIEW_KEY, view);
  writeStorageValue(window.sessionStorage, AUTH_VIEW_BACKUP_KEY, view);
};

export const updateAuthSession = (updates = {}) => {
  const current = loadAuthSession();
  if (!current) return null;
  const next = { ...current, ...updates };
  saveAuthSession(next, loadAuthView());
  return next;
};

export const clearAuthSession = () => {
  if (typeof window === 'undefined') return;

  [window.localStorage, window.sessionStorage].forEach((storage) => {
    writeStorageJson(storage, AUTH_SESSION_KEY, null);
    writeStorageJson(storage, AUTH_SESSION_BACKUP_KEY, null);
    writeStorageValue(storage, AUTH_VIEW_KEY, null);
    writeStorageValue(storage, AUTH_VIEW_BACKUP_KEY, null);
  });
};

const resolveEmployeeView = (savedView) => (savedView === 'admin' ? 'admin' : 'home');

const resolveApplicantView = (savedView) => {
  if (!savedView) return 'home';

  const applicantViews = ['application-waiting', 'apply', 'assessment', 'home'];
  if (savedView === 'applicant') return 'home';
  if (applicantViews.includes(savedView)) return savedView;
  return 'home';
};

export const getInitialAuthState = () => {
  const user = loadAuthSession();
  if (!user) {
    return {
      isLoggedIn: false,
      currentUser: null,
      currentView: 'home',
    };
  }

  const savedView = loadAuthView();
  const currentView = isApplicantUser(user)
    ? resolveApplicantView(savedView)
    : resolveEmployeeView(savedView);

  return {
    isLoggedIn: true,
    currentUser: user,
    currentView,
  };
};

export const syncAuthSessionSnapshot = (user, view = null) => {
  if (!user) return;
  const resolvedView = view || loadAuthView();
  if (resolvedView) {
    saveAuthSession(user, resolvedView);
    return;
  }
  saveAuthSession(user);
};
